/**
 * ContextManager: owns the single AudioContext (latencyHint 'interactive'), unlocks it from a user gesture,
 * keeps permanent unlock listeners, recovers from iOS 'interrupted', page visibility / focus changes, stalled
 * clocks and sample-rate flips (by rebuilding the context and asking the engine to rebuild its graphs), loads the
 * worklet modules, and plays a looping silent <audio> so the iOS silent switch does not mute Web Audio.
 */
import type { EngineState } from './types';
import { BASE_URL } from './stretch';

export interface ContextEvents {
  onState(state: EngineState): void;
  /** A fresh context replaced the old one: re-decode buffers, rebuild graphs, drop voices. */
  onRebuild(ctx: AudioContext, reason: string): Promise<void> | void;
}

export interface WorkletFlags { granular: boolean; pitchshift: boolean; capture: boolean }

const WORKLET_FILES: Array<[keyof WorkletFlags, string]> = [
  ['granular', 'granular.js'],
  ['pitchshift', 'pitchshift.js'],
  ['capture', 'capture.js'],
];

/** 0.1 s of 8 kHz 8-bit mono silence as a WAV data URI. */
export function silentWavDataUri(): string {
  const n = 800;
  const bytes = new Uint8Array(44 + n);
  const dv = new DataView(bytes.buffer);
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) bytes[o + i] = s.charCodeAt(i); };
  str(0, 'RIFF'); dv.setUint32(4, 36 + n, true); str(8, 'WAVE');
  str(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, 8000, true); dv.setUint32(28, 8000, true); dv.setUint16(32, 1, true); dv.setUint16(34, 8, true);
  str(36, 'data'); dv.setUint32(40, n, true);
  bytes.fill(128, 44);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return 'data:audio/wav;base64,' + btoa(bin);
}

function probeSampleRate(): number | null {
  try {
    const c = new AudioContext();
    const sr = c.sampleRate;
    void c.close().catch(() => { /* ignore */ });
    return sr;
  } catch {
    return null;
  }
}

export class ContextManager {
  ctx: AudioContext | null = null;
  state: EngineState = 'idle';
  readonly worklets: WorkletFlags = { granular: false, pitchshift: false, capture: false };
  private silent: HTMLAudioElement | null = null;
  private installed = false;
  private lastTime = -1;
  private stalled = 0;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private rebuilding: Promise<void> | null = null;
  private starting: Promise<void> | null = null;

  constructor(private readonly events: ContextEvents) {}

  get running(): boolean { return this.ctx?.state === 'running'; }
  get sampleRate(): number { return this.ctx?.sampleRate ?? 48000; }
  get current(): AudioContext {
    if (!this.ctx) throw new Error('AudioContext not started: call engine.start() from a user gesture');
    return this.ctx;
  }

  /** Call from a user gesture. Safe to call repeatedly. */
  async start(): Promise<void> {
    this.installListeners();
    this.startSilentAudio();
    if (!this.ctx) {
      if (!this.starting) this.starting = this.create('start').finally(() => { this.starting = null; });
      await this.starting;
    }
    await this.resume();
  }

  async resume(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const s = ctx.state as string;
    if (s === 'closed') { await this.rebuild('closed'); return; }
    if (s !== 'running') {
      try { await ctx.resume(); } catch { /* still locked; the next gesture will retry */ }
    }
    this.syncState();
  }

  /** Replace the context (after a stall, a sample-rate flip or a close). */
  rebuild(reason: string): Promise<void> {
    if (this.rebuilding) return this.rebuilding;
    this.rebuilding = (async () => {
      console.warn(`[tonist] rebuilding AudioContext (${reason})`);
      const old = this.ctx;
      this.ctx = null;
      if (old) { try { await old.close(); } catch { /* already closed */ } }
      await this.create(reason);
      await this.resume();
    })().finally(() => { this.rebuilding = null; });
    return this.rebuilding;
  }

  private async create(reason: string): Promise<void> {
    this.setState('starting');
    let ctx: AudioContext;
    try {
      ctx = new AudioContext({ latencyHint: 'interactive' });
    } catch (e) {
      this.setState('error');
      throw e;
    }
    this.ctx = ctx;
    ctx.addEventListener('statechange', () => { if (this.ctx === ctx) this.syncState(); });
    await this.loadWorklets(ctx);
    this.lastTime = ctx.currentTime;
    this.stalled = 0;
    this.startWatchdog();
    if (reason !== 'start') await this.events.onRebuild(ctx, reason);
    this.syncState();
  }

  private async loadWorklets(ctx: AudioContext): Promise<void> {
    if (!ctx.audioWorklet) {
      this.worklets.granular = this.worklets.pitchshift = this.worklets.capture = false;
      return;
    }
    for (const [key, file] of WORKLET_FILES) {
      try {
        await ctx.audioWorklet.addModule(`${BASE_URL}worklets/${file}`);
        this.worklets[key] = true;
      } catch (e) {
        this.worklets[key] = false;
        console.warn(`[tonist] worklet ${file} unavailable`, e);
      }
    }
  }

  private setState(s: EngineState): void {
    if (this.state === s) return;
    this.state = s;
    this.events.onState(s);
  }

  private syncState(): void {
    const s = (this.ctx?.state as string | undefined) ?? 'idle';
    if (s === 'idle') this.setState('idle');
    else if (s === 'running') this.setState('running');
    else if (s === 'closed') this.setState('error');
    else this.setState('interrupted'); // 'suspended' or iOS's non-standard 'interrupted'
  }

  private installListeners(): void {
    if (this.installed || typeof window === 'undefined') return;
    this.installed = true;
    const gesture = () => { void this.resume(); this.silent?.play().catch(() => { /* ignore */ }); };
    for (const ev of ['pointerup', 'touchend', 'keydown'] as const) {
      window.addEventListener(ev, gesture, { passive: true, capture: true });
    }
    const wake = () => { if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return; void this.onWake(); };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', wake);
    window.addEventListener('pageshow', wake);
    window.addEventListener('focus', wake);
  }

  private async onWake(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const wasRunning = ctx.state === 'running';
    await this.resume();
    if (!wasRunning && this.ctx === ctx) {
      // after an interruption (call, Bluetooth switch) the hardware rate may have flipped 44.1 ↔ 48 kHz
      const probe = probeSampleRate();
      if (probe && probe !== ctx.sampleRate) await this.rebuild('sampleRate');
    }
    this.silent?.play().catch(() => { /* ignore */ });
  }

  private startWatchdog(): void {
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = setInterval(() => this.check(), 1000);
  }

  private check(): void {
    const ctx = this.ctx;
    if (!ctx || this.rebuilding) return;
    if (ctx.state !== 'running') { this.stalled = 0; this.lastTime = ctx.currentTime; return; }
    if (ctx.currentTime <= this.lastTime) this.stalled++; else this.stalled = 0;
    this.lastTime = ctx.currentTime;
    if (this.stalled >= 3) { this.stalled = 0; void this.rebuild('stalled clock'); }
  }

  private startSilentAudio(): void {
    if (typeof document === 'undefined') return;
    try {
      if (!this.silent) {
        const a = document.createElement('audio');
        a.setAttribute('playsinline', '');
        a.setAttribute('webkit-playsinline', '');
        a.setAttribute('x-webkit-airplay', 'deny');
        a.loop = true;
        a.volume = 0.01;
        a.preload = 'auto';
        a.src = silentWavDataUri();
        this.silent = a;
      }
      this.silent.play().catch(() => { /* ignore: not allowed yet */ });
    } catch { /* ignore */ }
  }
}
